import React from "react";
import {
  Typography,
  Grid,
  Box,
  Button,
  Container,
  useTheme,
} from "@mui/material";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { useNavigate } from "react-router-dom";

// Icons
import SchoolIcon from "@mui/icons-material/School";
import { Psychology as BrainIcon } from "@mui/icons-material";
import { AssignmentTurnedIn as ClipboardIcon } from "@mui/icons-material";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import EventNoteIcon from "@mui/icons-material/EventNote";
import MicIcon from "@mui/icons-material/Mic";
import ImageIcon from "@mui/icons-material/Image";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import SecurityIcon from "@mui/icons-material/Security";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TranslateIcon from "@mui/icons-material/Translate";

const Functions = ({ selectedUserType }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDarkMode = theme.palette.mode === "dark";

  // USP data that contains all the function information
  const uspData = [
    {
      id: 1,
      heading: "Lektioner",
      description:
        "Generera lektioner med AI på bara några sekunder. Basera lektionen på valfritt underlag. Spara, skriv ut eller dela med elever.",
      link: "/lektioner",
      buttonText: "Läs mer",
      icon: <SchoolIcon sx={{ fontSize: 45 }} />,
      color: "#3B82F6",
      userType: 0, // Teacher only
    },
    {
      id: 2,
      heading: "Quiz",
      description:
        "Generera quiz baserat på valfritt underlag & välj mellan flera olika frågetyper. Skriv ut eller dela med elever och övervaka resultaten i realtid.",
      link: "/quiz",
      buttonText: "Läs mer",
      icon: <BrainIcon sx={{ fontSize: 45 }} />,
      color: "#9333EA",
    },
    {
      id: 3,
      heading: "Bedömning",
      description:
        "Bedöm elevtexter med AI. Välj bland våra egna bedömningsmatriser eller skapa en egen. Tillgång till resultattavla och mycket mer.",
      link: "/bedomning",
      buttonText: "Läs mer",
      icon: <ClipboardIcon sx={{ fontSize: 45 }} />,
      color: "#b45309",
      userType: 0, // Teacher only
    },
    {
      id: 4,
      heading: "Flashcards",
      description:
        "Generera flashcards med AI baserat på valfritt underlag. Lägg till bilder & dela med elever.",
      link: "/flashcards",
      buttonText: "Läs mer",
      icon: <ViewAgendaIcon sx={{ fontSize: 45 }} />,
      color: "#22C55E",
    },
    {
      id: 5,
      heading: "Lektionsplanering",
      description:
        "Låt Studera.ai generera lektionsplanering inom valfritt ämne. Bestäm längd på lektionen och vilka sektioner som ska ingå.",
      link: "/lektionsplanering",
      buttonText: "Läs mer",
      icon: <EventNoteIcon sx={{ fontSize: 45 }} />,
      color: "#444444",
      userType: 0, // Teacher only
    },
    {
      id: 6,
      heading: "Podcast",
      description:
        "Generera podcast med AI baserat på valfritt underlag. Välj mellan 50 olika språk & konfiguera ton och stil. Dela med elever.",
      link: "/podcast",
      buttonText: "Läs mer",
      icon: <MicIcon sx={{ fontSize: 45 }} />,
      color: "#5760c4",
    },
    {
      id: 7,
      heading: "Bilder",
      description:
        "Generera kompletterande bilder till ditt pedagogiska material med tillgång till ditt egen personliga arkiv.",
      link: "/bilder",
      buttonText: "Läs mer",
      icon: <ImageIcon sx={{ fontSize: 45 }} />,
      color: "#5760c4",
    },
    {
      id: 8,
      heading: "Assistenter",
      description:
        "Träna din egen AI-assistent och dela med elever, eller skapa en enklare AI-assistent och konfiguera hur den ska bete sig.",
      link: "/assistenter",
      buttonText: "Läs mer",
      icon: <SmartToyIcon sx={{ fontSize: 45 }} />,
      color: "#EF4444",
    },
    {
      id: 9,
      heading: "Högskoleprov",
      description:
        "Vi har alla gamla högskoleprov där eleverna kan få ledtrådar och öka sin förståelse genom att få stöd via AI.",
      link: "/hogskoleprovet",
      buttonText: "Läs mer",
      icon: <HistoryEduIcon sx={{ fontSize: 45 }} />,
      color: "#0f172a",
      userType: 1, // Student only
    },
    // {
    //   id: 11,
    //   heading: "Tryggt och säkert, även med AI-teknologi",
    //   description:
    //     "På Studera.ai prioriterar vi datasäkerhet och säkerställer att ingen användarinformation delas med våra AI-modeller.",
    //   link: "/app",
    //   buttonText: "Läs mer",
    //   icon: <SecurityIcon sx={{ fontSize: 45 }} />,
    // },
    // {
    //   id: 12,
    //   heading: "Lektioner i enlighet med Skolverkets kursplan",
    //   description:
    //     "Vi har koll på alla styrdokument från Skolverket, så du kan generera lektioner i enlighet med kursplan för respektive ämne och årskurs.",
    //   link: "/app",
    //   buttonText: "Läs mer",
    //   icon: <MenuBookIcon sx={{ fontSize: 45 }} />,
    //   userType: 0, // Teacher only
    // },
    // {
    //   id: 13,
    //   heading: "Stöd för över 50 språk",
    //   description:
    //     "Generera pedagogiskt material på över 50 språk. Vi vill att alla - oavsett kulturell bakgrund - ska kunna använda vår tjänst.",
    //   link: "/app",
    //   buttonText: "Läs mer",
    //   icon: <TranslateIcon sx={{ fontSize: 45 }} />,
    // },
  ];

  return (
    <Box
      sx={{
        py: 12,
        mt: 0,
        backgroundColor: isDarkMode
          ? theme.palette.background.default
          : "inherit",
      }}
    >
      <Container maxWidth="lg">
        <Box textAlign="center" mb={8}>
          <Typography
            gutterBottom
            sx={{
              mb: 2,
              mt: 3,
              fontWeight: "600",
              fontSize: "2.8rem",
              color: isDarkMode ? theme.palette.text.primary : "inherit",
            }}
          >
            Utforska
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {uspData
            .filter(
              (usp) =>
                usp.userType === undefined || usp.userType === selectedUserType,
            )
            .map((usp) => (
              <Grid item xs={12} sm={6} md={4} key={usp.id}>
                <Box
                  onClick={() => navigate(usp.link)}
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    overflow: "hidden",
                    position: "relative",
                    transition: "all 0.4s ease",
                    background: isDarkMode
                      ? theme.palette.background.paper
                      : "white",
                    boxShadow: isDarkMode
                      ? "0 5px 15px rgba(0,0,0,0.2)"
                      : "0 5px 15px rgba(0,0,0,0.05)",
                    "&:hover": {
                      transform: "translateY(-10px)",
                      boxShadow: isDarkMode
                        ? "0 20px 30px rgba(0,0,0,0.25)"
                        : "0 20px 30px rgba(0,0,0,0.1)",
                      "& .card-overlay": {
                        opacity: 1,
                      },
                      "& .btn": {
                        backgroundColor: "#9334e9",
                        color: "white",
                      },
                      "& .card-content": {
                        transform: "translateY(0)",
                      },
                    },
                    cursor: "pointer",
                  }}
                >
                  <Box
                    sx={{
                      p: 4,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      position: "relative",
                      zIndex: 2,
                    }}
                    className="card-content"
                  >
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: "20px",
                        mb: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: isDarkMode
                          ? `${usp.color}25`
                          : `${usp.color}15`,
                        color: usp.color,
                        fontSize: "2rem",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "rotate(10deg)",
                        },
                      }}
                    >
                      {usp.icon}
                    </Box>

                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        fontSize: "1.7rem",
                        color: isDarkMode
                          ? theme.palette.text.primary
                          : "inherit",
                      }}
                    >
                      {usp.heading}
                    </Typography>

                    <Typography
                      sx={{
                        color: isDarkMode
                          ? theme.palette.text.secondary
                          : "text.secondary",
                        mb: 4,
                        lineHeight: 1.7,
                        flex: 1,
                      }}
                    >
                      {usp.description}
                    </Typography>

                    <Button
                      className="btn"
                      variant="outlined"
                      color="primary"
                      endIcon={<KeyboardDoubleArrowRightIcon />}
                      sx={{
                        mt: "auto",
                        alignSelf: "flex-start",
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        transition: "all 0.4s ease",
                        fontSize: "0.9rem",
                        "&:hover": {
                          background:
                            theme.palette.mode === "dark"
                              ? "#9334e9"
                              : "#9334e9",
                          color: "white",
                          borderColor: "transparent",
                        },
                      }}
                    >
                      {usp.buttonText}
                    </Button>
                  </Box>

                  <Box
                    className="card-overlay"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: isDarkMode
                        ? `linear-gradient(180deg, rgba(0,0,0,0) 0%, ${usp.color}15 100%)`
                        : `linear-gradient(180deg, rgba(255,255,255,0) 0%, ${usp.color}05 100%)`,
                      opacity: 0,
                      transition: "all 0.4s ease",
                      zIndex: 1,
                    }}
                  />
                </Box>
              </Grid>
            ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Functions;
